import { store } from "src/store";
import {
  setBoundaryIndices,
  setIgnoreUpdateSelection,
  setSelectedCharacterStyleFilter,
  setSelectedGroupFilter,
  setSelectedStyleFilter,
} from "src/features/review-slice";
import { saveReviewProgress } from "src/services";
import { selectCharacterInfo, selectParagraphInfo } from "src/features/selectors";

export async function resetCharacterFiltersForSelection(controlId: string | null) {
  const state = store.getState();
  const { selectedCharacterStyleFilter, selectedCharacterOriginFilter } = state.review;
  if (selectedCharacterStyleFilter === null) return;

  const info = controlId ? selectCharacterInfo(state).find((c) => c.id === controlId) : undefined;
  if (
    info?.currentStyle === selectedCharacterStyleFilter &&
    (selectedCharacterOriginFilter === null ||
      info.formattingOrigin === selectedCharacterOriginFilter)
  ) {
    return;
  }

  store.dispatch(setIgnoreUpdateSelection(true));
  store.dispatch(setSelectedCharacterStyleFilter(null));
  await saveReviewProgress({
    selectedCharacterStyleFilter: null,
    selectedCharacterOriginFilter: null,
  });
}

export async function resetParagraphFiltersForSelection(
  paragraph: Pick<Word.Paragraph, "uniqueLocalId" | "style">
) {
  const state = store.getState();

  const {
    boundaryIndices,
    outlineMode,
    paragraphStyleRecords,
    selectedGroupFilter,
    selectedStyleFilter,
  } = state.review;

  let groupFilterMismatch = false;
  let outsideOfBoundaries = false;
  let styleFilterMismatch = false;

  if (boundaryIndices !== null) {
    const p = paragraphStyleRecords?.find((p) => p.uniqueLocalId === paragraph.uniqueLocalId);
    if (p) {
      outsideOfBoundaries = p.index < boundaryIndices[0] || p.index > boundaryIndices[1];
    }
  }

  if (selectedGroupFilter !== null) {
    const p = selectParagraphInfo(state).find((p) => p.uniqueLocalId === paragraph.uniqueLocalId);
    groupFilterMismatch = p?.category !== selectedGroupFilter;
  }

  if (selectedStyleFilter !== null && paragraph.style !== selectedStyleFilter) {
    styleFilterMismatch = true;
  }

  // Only set the flag alongside a filter change, or nothing consumes it and it swallows a later auto-select.
  if (outlineMode === "chapter" && outsideOfBoundaries) {
    store.dispatch(setIgnoreUpdateSelection(true));
    store.dispatch(setBoundaryIndices(null));
    store.dispatch(setSelectedGroupFilter(null));
    store.dispatch(setSelectedStyleFilter(null));
    await saveReviewProgress({
      boundaryIndices: null,
      selectedGroupFilter: null,
      selectedStyleFilter: null,
    });
    return;
  }

  if (outlineMode === "style" && groupFilterMismatch) {
    store.dispatch(setIgnoreUpdateSelection(true));
    store.dispatch(setSelectedGroupFilter(null));
    store.dispatch(setSelectedStyleFilter(null));
    await saveReviewProgress({ selectedGroupFilter: null, selectedStyleFilter: null });
    return;
  }

  if (styleFilterMismatch) {
    store.dispatch(setIgnoreUpdateSelection(true));
    store.dispatch(setSelectedStyleFilter(null));
    await saveReviewProgress({ selectedStyleFilter: null });
  }
}
